import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Card, CardBody, Flex, HStack, Heading, Tab, TabList,
  TabPanel, TabPanels, Tabs, Text, Tooltip, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, EditIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import PlanSettingsModal from '../components/rateplans/PlanSettingsModal';
import RulesPanel from '../components/rateplans/RulesPanel';
import LosTiersPanel from '../components/rateplans/LosTiersPanel';
import PreviewPanel from '../components/rateplans/PreviewPanel';
import SeasonsPanel from '../components/rateplans/SeasonsPanel';
import ratePlanService from '../services/ratePlanService';
import { RATE_TYPES, RATE_UNITS, apiError, tierRangeLabel } from '../components/rateplans/format';

// format.js owns these option lists; translate a key if it carries one.
const optionLabel = (t, item) => (item.labelKey ? t(item.labelKey) : item.label);

const PlanSummary = ({ plan, onEdit, onToggleActive, toggling }) => {
  const { t } = useTranslation();
  const typeLabel = Object.fromEntries(RATE_TYPES.map((x) => [x.value, optionLabel(t, x)]));
  const unitLabel = Object.fromEntries(RATE_UNITS.map((u) => [u.value, optionLabel(t, u)]));
  const tiers = (plan.los_discounts || []).filter((x) => x.is_active);
  const penalty = Number(plan.cancellation_penalty_percent);
  const conditions = [
    penalty > 0
      ? t('ratePlans.summary.freeCancellationWithPenalty', { hours: plan.cancellation_hours, percent: penalty })
      : t('ratePlans.summary.freeCancellation', { hours: plan.cancellation_hours }),
    plan.advance_purchase_days
      ? t('ratePlans.summary.advancePurchase', { days: plan.advance_purchase_days }) : null,
    plan.requires_advance_payment ? t('ratePlans.summary.payAtBooking') : null,
    plan.fallback_rate_plan_name
      ? t('ratePlans.summary.fallbackPlan', { plan: plan.fallback_rate_plan_name }) : null,
  ].filter(Boolean);
  return (
    <Card variant="outline" mb={4}>
      <CardBody>
        <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
          <Box>
            <HStack spacing={2} mb={1}>
              <Heading size="md">{plan.name}</Heading>
              <Text color="gray.500" fontSize="sm">{plan.code}</Text>
            </HStack>
            <HStack spacing={1} flexWrap="wrap">
              <Badge colorScheme={plan.is_active ? 'green' : 'gray'}>{plan.is_active ? t('ratePlans.badges.active') : t('ratePlans.badges.inactive')}</Badge>
              <Badge>{typeLabel[plan.rate_type] || plan.rate_type}</Badge>
              <Badge colorScheme="purple">{unitLabel[plan.rate_unit] || plan.rate_unit}</Badge>
              {plan.is_base_plan && <Badge colorScheme="blue">{t('ratePlans.badges.basePlan')}</Badge>}
              {!plan.is_public && <Badge colorScheme="orange">{t('ratePlans.badges.hiddenFromGuests')}</Badge>}
              <Badge colorScheme={plan.is_refundable ? 'teal' : 'red'}>
                {plan.is_refundable ? t('ratePlans.badges.refundable') : t('ratePlans.badges.nonRefundable')}
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.600" mt={2}>
              {conditions.join(' · ')}
            </Text>
            {tiers.length > 0 && (
              <Text fontSize="xs" color="green.700" mt={1}>
                {t('ratePlans.summary.longStay', {
                  tiers: tiers.slice().sort((a, b) => a.min_nights - b.min_nights)
                    .map((x) => t('ratePlans.summary.tierDiscount', {
                      range: tierRangeLabel(x), percent: Number(x.discount_percent),
                    })).join(' · '),
                })}
              </Text>
            )}
          </Box>
          <HStack>
            <Tooltip label={plan.is_active
              ? t('ratePlans.tooltips.deactivate')
              : t('ratePlans.tooltips.activate')}>
              <Button size="sm" variant="outline" onClick={onToggleActive} isLoading={toggling}>
                {plan.is_active ? t('ratePlans.buttons.deactivate') : t('ratePlans.buttons.activate')}
              </Button>
            </Tooltip>
            <Button size="sm" leftIcon={<EditIcon />} onClick={onEdit}>{t('ratePlans.buttons.editSettings')}</Button>
          </HStack>
        </Flex>
      </CardBody>
    </Card>
  );
};

const RatePlans = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [toggling, setToggling] = useState(false);
  const settings = useDisclosure();
  const toast = useToast();
  const shortUnit = { week: t('ratePlans.badges.unitWeek'), month: t('ratePlans.badges.unitMonth') };

  const loadSeasons = useCallback(async () => {
    setSeasons(await ratePlanService.getSeasons());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planList, rtList, seasonList] = await Promise.all([
        ratePlanService.getPlans(),
        ratePlanService.getRoomTypes(),
        ratePlanService.getSeasons(),
      ]);
      setPlans(planList);
      setRoomTypes(rtList.filter((rt) => rt.is_active !== false && rt.isActive !== false));
      setSeasons(seasonList);
      setSelectedId((current) => current ?? planList[0]?.id ?? null);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => plans.find((p) => p.id === selectedId) || null, [plans, selectedId]);

  const upsertPlan = (saved) => {
    if (!saved?.id) { load(); return; }
    setPlans((prev) => (prev.some((p) => p.id === saved.id)
      ? prev.map((p) => (p.id === saved.id ? saved : p))
      : [...prev, saved]));
    setSelectedId(saved.id);
  };

  const toggleActive = async () => {
    setToggling(true);
    try {
      upsertPlan(await ratePlanService.updatePlan(selected.id, { is_active: !selected.is_active }));
    } catch (err) {
      toast({ title: t('ratePlans.toasts.updateFailed'), description: apiError(err), status: 'error' });
    } finally {
      setToggling(false);
    }
  };

  return (
    <PageShell title={t('ratePlans.title')} subtitle={t('ratePlans.subtitle')}
      breadcrumbs={[{ label: t('ratePlans.title') }]} loading={loading} error={error}
      actions={(
        <Button size="sm" colorScheme="blue" leftIcon={<AddIcon />}
          onClick={() => { setEditingPlan(null); settings.onOpen(); }}>
          {t('ratePlans.buttons.newPlan')}
        </Button>
      )}>
      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab fontSize="sm">{t('ratePlans.tabs.ratePlans')}</Tab>
          <Tab fontSize="sm">{t('ratePlans.tabs.seasons')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Flex gap={4} align="flex-start" direction={{ base: 'column', lg: 'row' }}>
              <VStack align="stretch" spacing={2} w={{ base: '100%', lg: '260px' }} flexShrink={0}>
                {plans.length === 0 && !loading && (
                  <Text fontSize="sm" color="gray.500">{t('ratePlans.emptyState.noPlans')}</Text>
                )}
                {plans.map((p) => (
                  <Box key={p.id} as="button" textAlign="left" onClick={() => setSelectedId(p.id)}
                    p={3} borderWidth="1px" borderRadius="md"
                    bg={p.id === selectedId ? 'blue.50' : 'white'}
                    borderColor={p.id === selectedId ? 'blue.300' : 'gray.200'}
                    _hover={{ borderColor: 'blue.300' }} opacity={p.is_active ? 1 : 0.6}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{p.name}</Text>
                      <Text fontSize="xs" color="gray.500">{p.code}</Text>
                    </HStack>
                    <HStack spacing={1} mt={1}>
                      {!p.is_active && <Badge fontSize="2xs">{t('ratePlans.badges.inactive')}</Badge>}
                      {p.is_base_plan && <Badge colorScheme="blue" fontSize="2xs">{t('ratePlans.badges.base')}</Badge>}
                      {p.rate_unit !== 'night' && <Badge colorScheme="purple" fontSize="2xs">{shortUnit[p.rate_unit] || p.rate_unit}</Badge>}
                      {(p.los_discounts || []).some((t) => t.is_active) && (
                        <Badge colorScheme="green" fontSize="2xs">{t('ratePlans.badges.longStay')}</Badge>
                      )}
                    </HStack>
                  </Box>
                ))}
              </VStack>

              <Box flex={1} minW={0} w="100%">
                {selected ? (
                  <>
                    <PlanSummary plan={selected}
                      onEdit={() => { setEditingPlan(selected); settings.onOpen(); }}
                      onToggleActive={toggleActive} toggling={toggling} />
                    <Tabs size="sm" colorScheme="blue" isLazy>
                      <TabList>
                        <Tab>{t('ratePlans.tabs.pricingRules')}</Tab>
                        <Tab>{t('ratePlans.tabs.longStayDiscounts')}</Tab>
                        <Tab>{t('ratePlans.tabs.preview')}</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel px={0}>
                          <RulesPanel key={selected.id} plan={selected} roomTypes={roomTypes} seasons={seasons} />
                        </TabPanel>
                        <TabPanel px={0}>
                          <LosTiersPanel plan={selected} onSaved={upsertPlan} />
                        </TabPanel>
                        <TabPanel px={0}>
                          <PreviewPanel key={selected.id} plan={selected} seasons={seasons} />
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </>
                ) : (
                  !loading && <Text fontSize="sm" color="gray.500">{t('ratePlans.emptyState.pickPlan')}</Text>
                )}
              </Box>
            </Flex>
          </TabPanel>
          <TabPanel px={0}>
            <SeasonsPanel seasons={seasons} onChanged={loadSeasons} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <PlanSettingsModal isOpen={settings.isOpen} onClose={settings.onClose}
        plan={editingPlan} plans={plans} onSaved={upsertPlan} />
    </PageShell>
  );
};

export default RatePlans;
